import * as React from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

type Tipo = "date" | "month" | "datetime-local";

// O valor do campo nativo («2026-12-20», «2026-12», «2026-12-20T18:30») escrito do nosso jeito.
function noNossoFormato(tipo: Tipo, valor: string): string | null {
  const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?(?:T(\d{2}:\d{2}))?/.exec(valor);
  if (!m) return null;
  if (tipo === "month") return `${m[2]}/${m[1]}`;
  if (!m[3]) return null;
  const data = `${m[3]}/${m[2]}/${m[1]}`;
  if (tipo === "date") return data;
  return m[4] ? `${data} ${m[4]}` : null;
}

const VAZIO: Record<Tipo, string> = { date: "DD/MM/AAAA", month: "MM/AAAA", "datetime-local": "DD/MM/AAAA HH:MM" };

// Campo de data com o calendário do aparelho, mas sempre escrito DD/MM/AAAA (Pedro, 27/10/2026: «somos brasileiros»).
// O texto do próprio campo segue a língua do aparelho (num celular em inglês, «mm/dd/yyyy», e a data escolhida também
// sai mês/dia): ele fica invisível, e por cima vai a data no nosso formato. Tocar no campo abre o calendário.
export function DataBr({
  tipo = "date",
  value,
  onChange,
  className,
  classeDoBloco,
  escrever,
  ...props
}: Omit<React.ComponentProps<"input">, "type" | "value" | "onChange"> & {
  tipo?: Tipo;
  value: string;
  onChange: (valor: string) => void;
  // Para o bloco que envolve o campo, quando ele é item de uma linha flex (ex.: «flex-1»).
  classeDoBloco?: string;
  // Outro texto por cima para este valor (null = o de sempre); o valor do campo não muda. Ex.: o aniversário sem o ano.
  escrever?: (valor: string) => string | null;
}) {
  const escrito = escrever?.(value) ?? noNossoFormato(tipo, value);
  // O tamanho da letra que a tela deu ao campo vale para a data escrita por cima.
  const tamanhoDaLetra = (className ?? "").split(/\s+/).filter((c) => /^(\w+:)?text-(xs|sm|base|lg)$/.test(c));
  return (
    <div className={cn("relative", classeDoBloco)}>
      <Input
        type={tipo}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => {
          try {
            e.currentTarget.showPicker?.();
          } catch {
            // Navegador sem showPicker (ou bloqueado): o campo nativo segue abrindo do jeito dele.
          }
        }}
        className={cn(
          // opacity, e não só a cor: o pedaço do campo em foco ganha o realce azul do aparelho, por cima da nossa data.
          // h-full: numa linha flex o bloco estica, e o campo estica junto (como esticava antes de ganhar o bloco).
          "h-full text-transparent caret-transparent [&::-webkit-datetime-edit]:opacity-0",
          className,
        )}
        {...props}
      />
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-y-0 left-3 flex items-center text-base md:text-sm",
          tamanhoDaLetra,
          !escrito && "text-muted-foreground",
          props.disabled && "opacity-50",
        )}
      >
        {escrito ?? VAZIO[tipo]}
      </span>
    </div>
  );
}
